import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Mail,
  ArrowRight,
  Plane,
  Globe,
  Map,
  MapPin,
  Compass,
  Navigation,
  Camera,
  Palmtree,
  KeyRound,
  CheckCircle2,
  ChevronLeft,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const emailSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
});

export default function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const { forgotPassword } = useAuth();

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: emailErrors },
  } = useForm({
    resolver: zodResolver(emailSchema),
  });

  const onSendResetEmail = async (data) => {
    setError("");
    setSuccessMsg("");
    setLoading(true);
    try {
      const res = await forgotPassword(data.email.toLowerCase().trim());
      setUserEmail(data.email);
      setSuccessMsg(
        res?.message ||
          "If an account with that email exists, a password reset link has been sent."
      );
      setSubmitted(true);
    } catch (err) {
      console.error("Forgot Password Error", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to request password reset. Please try again.";
      setError(Array.isArray(msg) ? msg.join(", ") : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 relative overflow-hidden font-sans">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-teal-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <Plane className="floating-icon absolute top-[10%] left-[5%] w-8 h-8 md:w-12 md:h-12 text-teal-400 opacity-30" />
        <Globe className="floating-icon absolute top-[20%] right-[10%] w-10 h-10 md:w-14 md:h-14 text-cyan-400 opacity-40" />
        <Map className="floating-icon absolute top-[70%] left-[8%] w-7 h-7 md:w-10 md:h-10 text-blue-400 opacity-35" />
        <MapPin className="floating-icon absolute top-[40%] right-[20%] w-6 h-6 md:w-9 md:h-9 text-teal-500 opacity-30" />
        <Compass className="floating-icon absolute bottom-[15%] right-[25%] w-9 h-9 md:w-12 md:h-12 text-cyan-500 opacity-35" />
        <Navigation className="floating-icon absolute top-[50%] left-[3%] w-8 h-8 md:w-11 md:h-11 text-blue-500 opacity-30" />
        <Camera className="floating-icon absolute bottom-[50%] left-[30%] w-7 h-7 md:w-10 md:h-10 text-teal-400 opacity-40" />
        <Palmtree className="floating-icon absolute top-[30%] left-[15%] w-8 h-8 md:w-11 md:h-11 text-cyan-400 opacity-30" />
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>

      {/* Main Card */}
      <div
        className={`w-full max-w-md relative bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-700 ${
          mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        {/* Header Section */}
        <div className="relative bg-gradient-to-br from-[#1A8F98] to-[#0b959c] p-8 pb-12">
          <div className="absolute top-4 right-4 opacity-20">
            <KeyRound className="w-16 h-16 text-white transform -rotate-12" />
          </div>

          <div
            className={`text-center relative z-10 transition-all duration-700 delay-200 ${
              mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
            }`}
          >
            <h1 className="text-3xl font-bold text-white mb-2">Forgot Password?</h1>
            <p className="text-cyan-100 text-sm">
              Don't worry! Enter your email and we'll send you a password reset link.
            </p>
          </div>

          {/* Wave Effect */}
          <div className="absolute -bottom-1.5 left-0 right-0">
            <svg viewBox="0 0 1440 120" className="w-full h-12">
              <path
                fill="#ffffff"
                d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"
              ></path>
            </svg>
          </div>
        </div>

        {/* Form Section */}
        <div className="p-8 pt-4">
          {submitted ? (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">Check Your Inbox</h2>
              <p className="text-sm text-gray-600 leading-relaxed">{successMsg}</p>
              <div className="bg-blue-50 border border-blue-100 text-blue-800 text-xs p-3.5 rounded-xl text-left">
                📩 We sent a password reset link to <strong>{userEmail}</strong>. The link will expire in 60 minutes.
              </div>
              <button
                onClick={() => setSubmitted(false)}
                className="text-xs text-teal-600 hover:text-teal-700 font-medium underline"
              >
                Didn't get the email? Try again
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmitEmail(onSendResetEmail)} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 group-hover:text-[#1A8F98] transition-colors" />
                  <input
                    type="email"
                    {...registerEmail("email")}
                    placeholder="Enter your registered email"
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#1A8F98] focus:ring-2 focus:ring-[#1A8F98]/20 transition-all duration-300 outline-none hover:border-gray-300"
                  />
                </div>
                {emailErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{emailErrors.email.message}</p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#1A8F98] to-[#0b959c] text-white font-semibold py-3 rounded-xl hover:shadow-lg hover:scale-[1.02] active:scale-95 transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <span>Send Reset Link</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Back to Login */}
          <div className="text-center pt-6">
            <Link
              to="/login"
              className="text-gray-500 hover:text-[#1A8F98] font-medium transition-colors flex items-center justify-center gap-2 mx-auto no-underline text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Back to Login</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
